import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AppModal,
  AppCheckbox,
  AppSelect,
  AppTabs,
  AppButton,
  AppRadio,
  AppTextArea,
  AppTextField,
} from '@/components/atoms';
import useAppModal from '@/hooks/useAppModal';
import { FORM_MESSAGES } from '@/constants/formMessages';
import { formatJapaneseDate } from '@/utils/dateFormat';
import type {
  PendingVisitData,
  VisitClinicMassager,
  VisitClinicConsent,
  VisitClinicDayVisit,
} from '@/types/visitClinic.types';
import VisitErrorMessageList from '@/components/molecules/VisitErrorMessageList';

interface OutcomeTypeOption {
  id: number;
  name: string;
}

export interface VisitClinicDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** YYYY-MM-DD */
  date: string;
  current: PendingVisitData;
  /** Server-side visit record for the selected day (往診編集 readonly fields). */
  rawVisit?: VisitClinicDayVisit | null;
  massagers: VisitClinicMassager[];
  outcomeTypes: OutcomeTypeOption[];
  consents: VisitClinicConsent[];
  /** Whether the selected day is the first_treatment_date (初療日) */
  isFirstTreatmentDay?: boolean;
  /** BD §2.1: initial inspection fee prices (single はり or きゅう / combined both) */
  initialInspectionPrices?: { single: number; combined: number };
  /** Apply changes to parent's pending state */
  onApply: (update: Partial<PendingVisitData>) => void;
  /** Locked or error → show message + disable all + 適用 just closes without saving */
  disabled?: boolean;
  isLocked?: boolean;
}

const LABEL_BG = {
  acupuncture: '#FFFF99',
  moxibustion: '#CCFFCC',
  electric: '#FFCC99',
} as const;

function hasConsentOnDate(date: string, consents: VisitClinicConsent[]): boolean {
  return consents.some(c => {
    if (!c.treatment_term_from || !c.treatment_term_to) return false;
    return date >= c.treatment_term_from && date <= c.treatment_term_to;
  });
}

function consentAllows(
  date: string,
  consents: VisitClinicConsent[],
  key: 'has_acupuncture' | 'has_moxibustion' | 'has_electric',
): boolean {
  return consents.some(c => {
    if (!c.treatment_term_from || !c.treatment_term_to) return false;
    if (date < c.treatment_term_from || date > c.treatment_term_to) return false;
    return c[key] === undefined || c[key] === true;
  });
}

/** Resolve license flags from the currently selected massager (draft.massager_id) */
function getMassagerLicense(
  massagerId: number,
  massagers: VisitClinicMassager[],
): { hasAcupuncture: boolean; hasMoxibustion: boolean } {
  const m = massagers.find(x => x.massager_id === massagerId);
  return {
    hasAcupuncture: m?.has_acupuncture_license ?? true,
    hasMoxibustion: m?.has_moxibustion_license ?? true,
  };
}

const VisitClinicDetailModal: React.FC<VisitClinicDetailModalProps> = ({
  isOpen,
  onClose,
  date,
  current,
  rawVisit,
  massagers,
  outcomeTypes,
  consents,
  isFirstTreatmentDay = false,
  initialInspectionPrices,
  onApply,
  disabled = false,
  isLocked = false,
}) => {
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const modal = useAppModal();

  const isHouseCall =
    current.previous_visit_id !== null && current.previous_visit_id !== -1;

  const allDisabled = disabled || isLocked || !hasConsentOnDate(date, consents);

  const [draft, setDraft] = useState<PendingVisitData>(current);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const init = { ...current };
      if (isFirstTreatmentDay && !init.is_initial_inspection_fee) {
        init.is_initial_inspection_fee = 1;
      }
      setDraft(init);
      setIsDirty(false);
    }
  }, [isOpen, current, isFirstTreatmentDay]);

  // BD §2.7: re-check license when 施術担当者 changes
  const { hasAcupuncture, hasMoxibustion } = useMemo(
    () => getMassagerLicense(draft.massager_id, massagers),
    [draft.massager_id, massagers],
  );

  // BD §2.7: when a treatment is disabled (license/consent), it is treated as
  // unchecked for 初検料 (2.1) + 適用 payload, but the original draft value is
  // preserved so switching back to a licensed massager restores the state.

  const hasConsent = useMemo(() => hasConsentOnDate(date, consents), [date, consents]);

  const acupunctureDisabled =
    allDisabled || !hasAcupuncture || !consentAllows(date, consents, 'has_acupuncture');
  const moxibustionDisabled =
    allDisabled || !hasMoxibustion || !consentAllows(date, consents, 'has_moxibustion');
  const electricDisabled =
    allDisabled || !consentAllows(date, consents, 'has_electric') || (acupunctureDisabled && moxibustionDisabled);

  // Effective values: a disabled treatment is treated as 0 (for 初検料 calc + 適用 payload)
  // but the underlying draft value is preserved so switching massager back restores it.
  const effAcupuncture = acupunctureDisabled ? 0 : draft.is_acupuncture;
  const effMoxibustion = moxibustionDisabled ? 0 : draft.is_moxibustion;
  const effElectric = electricDisabled ? 0 : draft.electric_treatment;

  const massagerOptions = useMemo(
    () => massagers.map(m => ({ value: m.id, label: m.label })),
    [massagers],
  );

  const outcomeOptions = useMemo(
    () => outcomeTypes.map(o => ({ value: o.id, label: o.name })),
    [outcomeTypes],
  );

  const update = <K extends keyof PendingVisitData>(key: K, value: PendingVisitData[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  // BD §2.3-2.4: はり+きゅう at least one required
  const handleApply = useCallback(() => {
    if (allDisabled) {
      onClose();
      return;
    }
    if (!effAcupuncture && !effMoxibustion) {
      // BD §2.3-2.4: popup system dialog, keep modal open, don't save.
      // Applies to both 詳細編集 and 往診編集 flows — is_acupuncture / is_moxibustion
      // are saved on the visit regardless of house-call state.
      modal.warning({
        title: 'はり・きゅうのどちらかにチェックをいれてください。',
        okText: 'OK',
      });
      return;
    }
    onApply({
      // A2 parity: massager_id is NOT updated for 往診 visits (previous_visit_id != -1)
      ...(!isHouseCall && {
        massager_id: draft.massager_id,
        clinic_location_id: draft.clinic_location_id,
      }),
      is_initial_inspection_fee: draft.is_initial_inspection_fee,
      is_acupuncture: effAcupuncture,
      is_moxibustion: effMoxibustion,
      electric_treatment: effElectric,
      outcome_type_id: draft.outcome_type_id,
      massager_remarks: draft.massager_remarks,
    });
    onClose();
  }, [allDisabled, isHouseCall, draft, onApply, onClose]);

  // BD §3.5: close (X) / キャンセル → only show confirm when user made changes
  const handleCancel = useCallback(() => {
    if (!isDirty) {
      onClose();
      return;
    }
    modal.confirm({
      title: FORM_MESSAGES.DISCARD_CHANGES_TITLE,
      content: FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION,
      okText: FORM_MESSAGES.CONFIRM_OK,
      cancelText: FORM_MESSAGES.CONFIRM_CANCEL,
      onOk: () => onClose(),
    });
  }, [isDirty, modal, onClose]);

  const handleJumpToVisitsSchedule = () => {
    // Persistence + URL both use (massager_id, clinic_location_id) instead of CLM id.
    // Canonical 3-segment URL: /visits/schedule/:massagerId/:clinicLocationId/:visitdate.
    // 2-segment fallback when no location is available: /visits/schedule/:massagerId/:visitdate
    // (VisitSchedulePage will pick the first location for that massager).
    const massagerId = current.massager_id || 0;
    const locationId = rawVisit?.clinic_location_id ?? current.clinic_location_id ?? null;
    if (massagerId > 0 && locationId !== null) {
      navigate(`/${tenant}/visits/schedule/${massagerId}/${locationId}/${date}`);
      return;
    }
    navigate(`/${tenant}/visits/schedule/${massagerId}/${date}`);
  };

  const titleText = `${formatJapaneseDate(date)} 詳細編集`;

  const rowCellClass =
    'border border-gray-300 text-center font-medium py-1 px-3 w-[110px]';
  const checkCellClass =
    'border border-gray-300 text-center py-1 px-3';

  const treatmentRow = (
    label: string,
    bg: string,
    checked: boolean,
    onChange: (next: boolean) => void,
    isDisabled: boolean,
  ) => (
    <tr>
      <td className={rowCellClass} style={{ backgroundColor: bg }}>{label}</td>
      <td className={checkCellClass}>
        <AppCheckbox
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          disabled={isDisabled}
        />
      </td>
    </tr>
  );

  const editTab = (
    <div className="flex flex-col gap-3 py-1">
      {isLocked && (
        <p className="text-red-600 text-sm">ロックされているため、編集及び削除はできません。</p>
      )}
      <VisitErrorMessageList
        errorItems={rawVisit?.error_items}
        errorMessages={rawVisit?.error_messages}
        tenant={tenant}
        className="text-sm list-disc"
      />


      {/* BD §2.1 + §2.2: 初検料 + 初検 checkbox — only when day = first_treatment_date */}
      {isFirstTreatmentDay && initialInspectionPrices && (
        <div className="flex items-center gap-2 text-sm">
          <span>初検料</span>
          <AppTextField
            disabled
            value={
              String((effAcupuncture && effMoxibustion)
                ? initialInspectionPrices.combined
                : initialInspectionPrices.single)
            }
            className="w-[260px]"
          />
          <AppCheckbox
            checked={!!draft.is_initial_inspection_fee}
            onChange={e => update('is_initial_inspection_fee', e.target.checked ? 1 : 0)}
            disabled={allDisabled}
          />
        </div>
      )}

      <table className="border-collapse">
        <tbody>
          {treatmentRow(
            'はり',
            LABEL_BG.acupuncture,
            !!effAcupuncture,
            v => update('is_acupuncture', v ? 1 : 0),
            acupunctureDisabled,
          )}
          {treatmentRow(
            'きゅう',
            LABEL_BG.moxibustion,
            !!effMoxibustion,
            v => update('is_moxibustion', v ? 1 : 0),
            moxibustionDisabled,
          )}
          {treatmentRow(
            '電(鍼・温)',
            LABEL_BG.electric,
            !!effElectric,
            v => update('electric_treatment', v ? 1 : 0),
            electricDisabled,
          )}
        </tbody>
      </table>

      <div className="grid grid-cols-[90px_1fr] gap-x-3 gap-y-2 items-center mt-2">
        <span className="text-sm">転帰</span>
        <AppSelect
          value={draft.outcome_type_id || undefined}
          onChange={val => update('outcome_type_id', (val as number | null) || null)}
          options={outcomeOptions}
          placeholder=""
          disabled={allDisabled}
          allowClear
          className="w-40"
        />

        <span className="text-sm">施術担当者</span>
        <AppSelect
          value={(() => {
            const entry = massagers.find(
              m => m.massager_id === draft.massager_id
                && m.clinic_location_id === draft.clinic_location_id,
            ) ?? massagers.find(m => m.massager_id === draft.massager_id);
            return entry?.id ?? undefined;
          })()}
          onChange={val => {
            const entry = massagers.find(m => m.id === val);
            if (entry) {
              setDraft(prev => ({
                ...prev,
                massager_id: entry.massager_id,
                clinic_location_id: entry.clinic_location_id,
              }));
              setIsDirty(true);
            }
          }}
          options={massagerOptions}
          placeholder=""
          disabled={allDisabled || isHouseCall}
          className="w-full"
        />

        <span className="text-sm">施術メモ</span>
        <AppTextArea
          value={draft.massager_remarks ?? ''}
          onChange={e => update('massager_remarks', e.target.value || null)}
          maxLength={255}
          disabled={allDisabled || isLocked}
          placeholder="施術メモ"
          className="w-full"
        />
      </div>
    </div>
  );

  const houseCallTab = (
    <div className="flex flex-col gap-3 py-1">
      {!hasConsent && (
        <p className="text-red-600 text-sm">施術日に対応する同意書がありません。</p>
      )}
      <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 items-center text-sm">
        {/* BD §3.1: 訪問日時 — format H時mm分, read-only */}
        <span className="text-gray-600">訪問日時</span>
        <AppTextField
          disabled
          value={(() => {
            const d = rawVisit?.visit_datetime_from;
            if (!d) return '';
            const dt = new Date(d);
            if (Number.isNaN(dt.getTime())) return '';
            return `${dt.getHours()}時${String(dt.getMinutes()).padStart(2, '0')}分`;
          })()}
          className="w-full"
        />

        {/* BD §3.2: 所要時間 — read-only */}
        <span className="text-gray-600">所要時間</span>
        <AppTextField
          disabled
          value={rawVisit?.visit_minutes != null ? `${rawVisit.visit_minutes}分` : ''}
          className="w-full"
        />

        {/* BD §3.3: 診療区分 — fixed radio 保険診療 */}
        <span className="text-gray-600">診療区分</span>
        <AppRadio checked disabled>保険診療</AppRadio>
      </div>
      <div>
        <AppButton type="primary" onClick={handleJumpToVisitsSchedule}>
          訪問入力画面
        </AppButton>
      </div>
    </div>
  );

  // Per A2 legacy (visit_clinic/schedule.blade.php): 詳細編集 is always selectable;
  // only 往診編集 toggles based on whether the visit has 往診 data.
  const tabItems = [
    { key: 'edit', label: '詳細編集', children: editTab },
    { key: 'houseCall', label: '往診編集', disabled: !isHouseCall, children: houseCallTab },
  ];

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={titleText}
      maxWidth="max-w-xl"
      okText="適用"
      cancelText="キャンセル"
      onOk={handleApply}
    >
      {/* BD §1.3 rule: when a day has errors, auto-surface 詳細編集 tab even on 往診 rows */}
      <AppTabs
        items={tabItems}
        defaultActiveKey={
          (rawVisit?.error_messages?.length ?? 0) > 0 || (rawVisit?.error_items?.length ?? 0) > 0
            ? 'edit'
            : isHouseCall
              ? 'houseCall'
              : 'edit'
        }
      />
    </AppModal>
  );
};

export default VisitClinicDetailModal;
